import { useNavigate } from '@tanstack/react-router'
import { decodeJwt } from '@/api/hooks/user.hooks'
import { UserRole, AppointmentTimeZone } from '@/api/enums'
import { STORAGE_KEYS } from '@/constants/storage'
import { authCoordinator } from '@/auth/authCoordinator'
import { requestAppointment } from '@/application/booking/requestAppointment'
import { callApi } from '@/api/callEndpoint'
import {
  customerEndpoints,
  type PaymentMethodSummary,
} from '@/api/contracts/customer.contract'

// A first-time patient has no card on file, and the API rejects appointment
// creation until one exists. So instead of replaying the booking straight into
// creation, only auto-create when we can confirm a card is present. Any other
// outcome (no card, or the check failed) sends them back to the booking form to
// add a card there.
async function hasSavedCard(): Promise<boolean> {
  try {
    const summary = await callApi<PaymentMethodSummary>(
      customerEndpoints.paymentMethod,
    )
    return summary.hasPaymentMethod
  } catch {
    return false
  }
}

function getPostLoginDestination(roles: UserRole[], redirect?: string): string {
  if (redirect) return redirect
  if (roles.includes(UserRole.Patient)) return '/client-area'
  if (roles.some((r) => [UserRole.Admin, UserRole.SuperAdmin, UserRole.PracticeAdministrator].includes(r))) return '/admin'
  if (roles.includes(UserRole.Specialist)) return '/specialist'
  return '/'
}

/**
 * Where a freshly authenticated user lands, shared by /login and /signup. Signup
 * goes through the very same branch: a brand-new patient never has a saved card,
 * so a pending booking always routes them back to /booking to enter one.
 */
export function usePostAuthRedirect(redirect?: string) {
  const navigate = useNavigate()

  return async () => {
    const payload = authCoordinator.getPendingBooking()
    if (payload) {
      if (await hasSavedCard()) {
        authCoordinator.clearPendingBooking()
        const appointmentId = await requestAppointment(payload)
        navigate({ to: '/confirmation', search: { appointmentId }, replace: true })
        return
      }
      // No card yet: keep the pending booking so the form rehydrates, and send
      // the patient back to complete their (now card-gated) first appointment.
      const stored = authCoordinator.getPendingBookingSearch() ?? {}
      navigate({
        to: '/booking',
        search: {
          specialistId: stored.specialistId ?? '',
          timeSlotId: stored.timeSlotId ?? '',
          locationId: stored.locationId,
          locationAddress: stored.locationAddress,
          appointmentDate: stored.appointmentDate ?? '',
          timeZone:
            stored.timeZone === AppointmentTimeZone.EST
              ? AppointmentTimeZone.EST
              : undefined,
          doctor: stored.doctor,
        },
        replace: true,
      })
      return
    }
    const token = sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
    const decoded = token ? decodeJwt(token) : null
    navigate({ to: getPostLoginDestination(decoded?.roles ?? [], redirect), replace: true })
  }
}
